import { useEffect, useState } from "react";
import {
  Grid,
  Card,
  Modal,
  TextField,
  Box,
  CircularProgress,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import PropTypes from "prop-types";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";

import api from "../../utils/axiosConfig";
import { fadeInUp, fadeInDown, modalScaleIn } from "../../utils/animations";

const API_BASE_URL = "/api/types-textes";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "95vw", sm: "85vw", md: 450 },
  bgcolor: "background.paper",
  borderRadius: 2,
  boxShadow: 24,
  p: { xs: 2, sm: 3, md: 4 },
  maxHeight: "90vh",
  overflowY: "auto",
};

function TypesTextes() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [open, setOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);

  const [formData, setFormData] = useState({
    id: null,
    type: "",
    description: "",
  });

  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    message: "",
    onConfirm: null,
  });

  // 1. Définition des colonnes de base
  const columns = [
    { Header: "ID", accessor: "id", width: "10%" },
    { Header: "Type / Désignation", accessor: "type", align: "left", wrap: true },
    { Header: "Description", accessor: "description", align: "left", wrap: true },
  ];

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await api.get(API_BASE_URL + "/");
      setRows(response.data);
    } catch (error) {
      setSnackbar({ open: true, message: "Erreur de chargement des données.", severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleOpen = () => {
    setIsEdit(false);
    setFormData({ id: null, type: "", description: "" });
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleEdit = (rowData) => {
    setIsEdit(true);
    setFormData(rowData);
    setOpen(true);
  };

  const handleDelete = (id) => {
    setConfirmDialog({
      open: true,
      message: "Voulez-vous vraiment supprimer ce type de texte ?",
      onConfirm: async () => {
        try {
          await api.delete(`${API_BASE_URL}/${id}`);
          setSnackbar({ open: true, message: "Type de texte supprimé !", severity: "success" });
          fetchData();
        } catch (error) {
          setSnackbar({ open: true, message: "Erreur lors de la suppression.", severity: "error" });
        }
      },
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      if (isEdit) {
        await api.put(`${API_BASE_URL}/${formData.id}`, formData);
        setSnackbar({ open: true, message: "Type mis à jour avec succès !", severity: "success" });
      } else {
        await api.post(API_BASE_URL + "/", formData);
        setSnackbar({ open: true, message: "Type ajouté avec succès !", severity: "success" });
      }
      handleClose();
      fetchData();
    } catch (error) {
      setSnackbar({ open: true, message: "Erreur lors de l'enregistrement.", severity: "error" });
    } finally {
      setIsSubmitting(false);
    }
  };

  // 2. Composant interne pour les actions (Modifier/Supprimer)
  const CellWithActions = ({ row }) => (
    <MDBox display="flex" justifyContent="center">
      <MDButton color="info" size="small" onClick={() => handleEdit(row.original)} sx={{ mr: 1 }}>
        Modifier
      </MDButton>
      <MDButton color="error" size="small" onClick={() => handleDelete(row.original.id)}>
        Supprimer
      </MDButton>
    </MDBox>
  );

  CellWithActions.propTypes = {
    row: PropTypes.shape({
      original: PropTypes.shape({
        id: PropTypes.number,
        type: PropTypes.string,
        description: PropTypes.string,
      }).isRequired,
    }).isRequired,
  };

  // 3. Fusion des colonnes avec les actions (Résout l'erreur ReferenceError)
  const columnsWithActions = [
    ...columns,
    {
      Header: "Actions",
      accessor: "actions",
      align: "center",
      Cell: CellWithActions,
    },
  ];

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card sx={{ animation: `${fadeInUp} 0.5s ease-out both` }}>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="purple"
                borderRadius="lg"
                coloredShadow="dark"
                display="flex"
                justifyContent="space-between"
                alignItems="flex-start"
                sx={{ animation: `${fadeInDown} 0.5s ease-out both` }}
              >
                <MDBox>
                  <MDTypography variant="h6" sx={{ color: "#FFD740" }}>
                    Gestion des Types de Texte
                  </MDTypography>
                  <MDTypography
                    variant="caption"
                    sx={{ color: "rgba(255,255,255,0.72)", display: "block", mt: 0.3 }}
                  >
                    Gestion des catégories de textes réglementaires
                  </MDTypography>
                </MDBox>
                <MDButton variant="outlined" color="white" onClick={handleOpen}>
                  Nouveau Type
                </MDButton>
              </MDBox>
              <MDBox pt={3}>
                {loading ? (
                  <MDBox display="flex" justifyContent="center" p={5}>
                    <CircularProgress color="info" />
                  </MDBox>
                ) : (
                  <DataTable
                    table={{ columns: columnsWithActions, rows }}
                    isSorted
                    entriesPerPage
                    showTotalEntries
                    noEndBorder
                  />
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* MODAL DE SAISIE */}
      <Modal open={open} onClose={handleClose}>
        <Box sx={{ ...modalStyle, animation: `${modalScaleIn} 0.3s ease-out both` }}>
          <MDTypography variant="h6" mb={3}>
            {isEdit ? "Modifier le Type" : "Nouveau Type de Texte"}
          </MDTypography>
          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Nom du Type (ex: Loi, Décret...)"
              name="type"
              value={formData.type}
              onChange={handleChange}
              margin="normal"
              required
              inputProps={{ maxLength: 100 }}
            />
            <TextField
              fullWidth
              label="Description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              margin="normal"
              multiline
              rows={3}
            />
            <MDBox mt={4} display="flex" justifyContent="flex-end">
              <MDButton variant="text" color="secondary" onClick={handleClose} sx={{ mr: 1 }}>
                Annuler
              </MDButton>
              <MDButton type="submit" variant="gradient" color="purple" disabled={isSubmitting}>
                {isSubmitting ? "Envoi..." : "Enregistrer"}
              </MDButton>
            </MDBox>
          </form>
        </Box>
      </Modal>

      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}
      >
        <DialogTitle>Confirmation</DialogTitle>
        <DialogContent>
          <MDTypography variant="body2">{confirmDialog.message}</MDTypography>
        </DialogContent>
        <DialogActions>
          <MDButton
            onClick={() => setConfirmDialog({ ...confirmDialog, open: false })}
            color="secondary"
          >
            Annuler
          </MDButton>
          <MDButton
            onClick={() => {
              setConfirmDialog({ ...confirmDialog, open: false });
              confirmDialog.onConfirm?.();
            }}
            variant="gradient"
            color="warning"
          >
            Confirmer
          </MDButton>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
      <Footer />
    </DashboardLayout>
  );
}

export default TypesTextes;
